import React, { useState, useEffect } from 'react';
import { Meeting, MeetingDashboardKPIs } from './types';
import { meetingsService, SystemUserOption } from './meetingsService';
import { MeetingsMetricsCards } from './MeetingsMetricsCards';
import { MeetingsTable } from './MeetingsTable';
import { MeetingDetailModal } from './MeetingDetailModal';
import { MeetingCreateModal } from './MeetingCreateModal';

export const MeetingsPage: React.FC = () => {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [kpis, setKpis] = useState<MeetingDashboardKPIs | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');

  // Modals
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [list, stats] = await Promise.all([
        meetingsService.getMeetings({
          search: search || undefined,
          status: statusFilter || undefined,
          meeting_type_id: typeFilter || undefined
        }),
        meetingsService.getDashboardKPIs()
      ]);
      setMeetings(Array.isArray(list) ? list : []);
      setKpis(stats || null);
      if (selectedMeeting && Array.isArray(list)) {
        const reloaded = list.find(m => m.id === selectedMeeting.id);
        if (reloaded) setSelectedMeeting(reloaded);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load meetings data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [search, statusFilter, typeFilter]);

  const handleCreate = async (data: Partial<Meeting>, selectedParticipants?: SystemUserOption[]) => {
    try {
      const created = await meetingsService.createMeeting(data);
      if (created && created.id && selectedParticipants && selectedParticipants.length > 0) {
        for (const p of selectedParticipants) {
          try {
            await meetingsService.addParticipant(created.id, {
              user_id: p.id,
              user_name: p.display_name,
              email: p.email,
              participant_role: 'ATTENDEE',
              is_required: true,
              invitation_status: 'PENDING'
            });
          } catch (e) {
            console.warn('[MeetingsPage] addParticipant warning:', e);
          }
        }
      }
    } catch (err: any) {
      console.warn('[MeetingsPage] handleCreate warning:', err);
    }
    await loadData();
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Top Header */}
      <div className="page-header-banner">
        <div className="badge">QUALITY & GOVERNANCE</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1>Meetings & Management Review Boards</h1>
            <p>EASA/ICAO compliant SRB/SAG committee meetings, agendas, formal SoD minute approvals, and central action integration.</p>
          </div>
          <button
            onClick={() => setIsCreateOpen(true)}
            className="btn-primary"
            style={{ width: 'auto', padding: '10px 18px' }}
          >
            + Schedule New Meeting
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <MeetingsMetricsCards kpis={kpis} />

      {/* Filters */}
      <div style={{
        background: '#ffffff',
        padding: '16px',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        marginBottom: '20px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        alignItems: 'center'
      }}>
        <div style={{ flex: '1 1 260px' }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by meeting title, number, or location..."
            style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', boxSizing: 'border-box' }}
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#fff' }}
        >
          <option value="">All Statuses</option>
          <option value="DRAFT">Draft</option>
          <option value="SCHEDULED">Scheduled</option>
          <option value="INVITATIONS_SENT">Invitations Sent</option>
          <option value="PREPARATION">In Preparation</option>
          <option value="IN_PROGRESS">In Progress</option>
          <option value="COMPLETED">Completed</option>
          <option value="MINUTES_DRAFT">Draft Minutes</option>
          <option value="MINUTES_APPROVED">Minutes Approved</option>
          <option value="CLOSED">Closed</option>
          <option value="CANCELLED">Cancelled</option>
        </select>

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#fff' }}
        >
          <option value="">All Meeting Types</option>
          <option value="MT-SRB">Safety Review Board (SRB)</option>
          <option value="MT-SAG">Safety Action Group (SAG)</option>
          <option value="MT-MR">Management Review (MR)</option>
        </select>

        <button
          onClick={() => {
            setSearch('');
            setStatusFilter('');
            setTypeFilter('');
          }}
          style={{ background: '#f1f5f9', color: '#475569', border: 'none', padding: '8px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}
        >
          Reset Filters
        </button>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #f87171', color: '#b91c1c', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', fontSize: '13px' }}>
          {error}
        </div>
      )}

      <MeetingsTable
        meetings={meetings}
        selectedId={selectedMeeting?.id || null}
        onSelect={(m) => setSelectedMeeting(m)}
      />

      {selectedMeeting && (
        <MeetingDetailModal
          meeting={selectedMeeting}
          onClose={() => setSelectedMeeting(null)}
          onRefresh={loadData}
        />
      )}

      <MeetingCreateModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSubmit={handleCreate}
      />
    </div>
  );
};
